import { useState } from "react";
import { toast } from "sonner";
import { envFelder, spec2, unavailableText } from "@/types";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAgentConnectApiKey, useAgentConnectLogin, useAgentConnectClientCredentials, useAgentConnectEnv, useAgentStartOAuth, type OwnerConnectScope } from "@/api/mutations/connectors";
import type { AgentConnectorSummary } from "@/types";

interface Props {
  agentId: string;
  connector: AgentConnectorSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// #231 (UC-7): Per-Agent-Connect im Admin-UI. Scope "agent" = dediziertes Konto nur für
// diesen Agent; "owner" = Konto des Agent-Owners (alle seine Agents). Wert/Token write-only.
export function ConnectAgentConnectorDialog({ agentId, connector, open, onOpenChange }: Props) {
  const [scope, setScope] = useState<OwnerConnectScope>("agent");
  const [value, setValue] = useState("");
  const [loginUser, setLoginUser] = useState("");
  const [loginPass, setLoginPass] = useState("");
  // #336 (UC-1): Client-Credentials — zwei Secret-Felder, kein Browser-Flow.
  const [ccClientId, setCcClientId] = useState("");
  const [ccClientSecret, setCcClientSecret] = useState("");
  const [envValues, setEnvValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const connectApiKey = useAgentConnectApiKey(agentId);
  const connectLogin = useAgentConnectLogin(agentId);
  const connectCc = useAgentConnectClientCredentials(agentId);
  const connectEnv = useAgentConnectEnv(agentId);
  const startOAuth = useAgentStartOAuth(agentId);
  // #297: Der Master beschreibt, WIE verbunden wird — dieser Dialog rendert nur noch.
  // Damit faellt auch der zweite (admin-only) Katalog-Abruf weg, den es frueher nur
  // brauchte, um die Formularfelder zu erfahren.
  // #297 (Review HIGH-1/MED-4): fehlende ODER unbekannte Spec landen beide bei
  // "unavailable" — die UI raet nie und behauptet nie "kein Credential noetig".
  const spec = spec2(connector.connectSpec);
  const isOAuth = spec.method === "oauth";
  const isEnv = spec.method === "env";
  const isLogin = spec.method === "login";
  const isApiKey = spec.method === "api-key";
  const isClientCredentials = spec.method === "client-credentials";
  const envSchema = envFelder(spec);

  function close(o: boolean) {
    if (!o) { setValue(""); setLoginUser(""); setLoginPass(""); setCcClientId(""); setCcClientSecret(""); setEnvValues({}); setError(null); setScope("agent"); }
    onOpenChange(o);
  }

  function submitLogin() {
    setError(null);
    if (!loginUser.trim() || !loginPass) return setError("Benutzername + Passwort sind erforderlich");
    connectLogin.mutate(
      { connectorId: connector.connectorId, username: loginUser.trim(), password: loginPass, scope },
      {
        onSuccess: () => { toast.success(`${connector.name} verbunden (${scope})`); close(false); },
        onError: (err) => { const m = (err as Error).message; setError(m); toast.error(`Verbinden fehlgeschlagen: ${m}`); },
      },
    );
  }

  // #336 (UC-1): clientId + clientSecret write-only → Vault; den Token holt der Master.
  function submitClientCredentials() {
    setError(null);
    if (!ccClientId.trim() || !ccClientSecret.trim()) return setError("Client-ID + Client-Secret sind erforderlich");
    connectCc.mutate(
      { connectorId: connector.connectorId, clientId: ccClientId.trim(), clientSecret: ccClientSecret.trim(), scope },
      {
        onSuccess: () => { toast.success(`${connector.name} verbunden (${scope})`); close(false); },
        onError: (err) => { const m = (err as Error).message; setError(m); toast.error(`Verbinden fehlgeschlagen: ${m}`); },
      },
    );
  }

  function submitEnv() {
    setError(null);
    const vars: Record<string, string> = {};
    for (const [k, v] of Object.entries(envValues)) {
      if (k.trim() && v !== "") vars[k.trim()] = v;
    }
    // ALLE Pflichtfelder erzwingen — nicht nur „irgendeine Variable".
    // Zwei Gründe: (1) die Delivery laesst den Connector aus, wenn das binding-ENV oder
    // eine required extraEnv fehlt (ConnectorEnablementService.resolveDelivery) — der
    // Status meldete sonst „verbunden", das Tool erreicht den Agenten aber nie.
    // (2) `connectEnv` ERSETZT das Vault-Buendel; ein Nachtragen nur einer Variablen
    // wuerde die anderen still loeschen und ein laufendes Tool abschalten.
    const fehlend = envSchema.filter((s) => s.required && !vars[s.name]).map((s) => s.name);
    if (fehlend.length > 0) return setError(`Pflichtfelder fehlen: ${fehlend.join(", ")}`);
    if (Object.keys(vars).length === 0) return setError("Mindestens eine Variable mit Wert");
    connectEnv.mutate(
      { connectorId: connector.connectorId, vars, scope },
      {
        onSuccess: () => { toast.success(`${connector.name} verbunden (${scope})`); close(false); },
        onError: (err) => { const m = (err as Error).message; setError(m); toast.error(`Verbinden fehlgeschlagen: ${m}`); },
      },
    );
  }

  function submitApiKey() {
    setError(null);
    if (!value.trim()) return setError("Wert ist erforderlich");
    connectApiKey.mutate(
      { connectorId: connector.connectorId, value: value.trim(), scope },
      {
        onSuccess: () => { toast.success(`${connector.name} verbunden (${scope})`); close(false); },
        onError: (err) => { const m = (err as Error).message; setError(m); toast.error(`Verbinden fehlgeschlagen: ${m}`); },
      },
    );
  }

  function submitOAuth() {
    setError(null);
    startOAuth.mutate(
      { connectorId: connector.connectorId, scope },
      {
        onSuccess: ({ authorizeUrl }) => {
          window.open(authorizeUrl, "_blank", "noopener");
          toast.message("OAuth-Login im neuen Tab geöffnet — nach Freigabe hier aktualisieren.");
          close(false);
        },
        onError: (err) => { const m = (err as Error).message; setError(m); toast.error(`OAuth-Start fehlgeschlagen: ${m}`); },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="connect-agent-connector-dialog">
        <DialogHeader>
          <DialogTitle>{connector.name} verbinden</DialogTitle>
          <DialogDescription>
            Für diesen Agent ({agentId}). Secret liegt verschlüsselt im Vault — nie sichtbar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Scope</Label>
            <Select value={scope} onValueChange={(v) => setScope(v as OwnerConnectScope)}>
              <SelectTrigger data-testid="connect-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="agent">nur dieser Agent (agent-scope)</SelectItem>
                <SelectItem value="owner">Owner-Konto (alle Agents des Owners)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-text3">
              Präzedenz bei der Nutzung: agent &gt; owner &gt; platform.
            </p>
          </div>

          {isOAuth ? (
            <p className="text-[12.5px] text-text2">
              OAuth: Button öffnet den Login des Anbieters in einem neuen Tab. Nach „Zugriff erlauben"
              landet der Token (scope-spezifisch) im Vault.
            </p>
          ) : isEnv ? (
            <div className="space-y-2" data-testid="connect-env-fields">
              {/* #297: envSchema ist hier garantiert nicht leer — connectSpec liefert
                  "env" nur MIT Feldern (sonst "none"). Der frueher noetige Leer-Zweig ist weg. */}
              {(
                envSchema.map((spec) => (
                  <div key={spec.name} className="space-y-1">
                    <Label>
                      {spec.label || spec.name} {spec.required && <span className="text-danger">*</span>}{" "}
                      <span className="mono text-[11px] text-text3">{spec.name}{spec.secret ? " · secret" : ""}</span>
                    </Label>
                    <Input
                      type={spec.secret ? "password" : "text"}
                      value={envValues[spec.name] ?? ""}
                      onChange={(e) => setEnvValues((v) => ({ ...v, [spec.name]: e.target.value }))}
                      placeholder={spec.placeholder || ""}
                      className="mono text-[12px]"
                    />
                  </div>
                ))
              )}
            </div>
          ) : isLogin ? (
            <div className="space-y-2" data-testid="connect-login-fields">
              <p className="text-[11.5px] text-text3">user/pw write-only → Vault. Der Master holt + refresht den Token (LLM sieht ihn nie).</p>
              <div className="space-y-1">
                <Label htmlFor="connect-login-user">Benutzername</Label>
                <Input id="connect-login-user" value={loginUser} onChange={(e) => setLoginUser(e.target.value)} placeholder="user" className="mono text-[12px]" data-testid="connect-login-user" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="connect-login-pass">Passwort</Label>
                <Input id="connect-login-pass" type="password" value={loginPass} onChange={(e) => setLoginPass(e.target.value)} placeholder="••••••" className="mono text-[12px]" data-testid="connect-login-pass" />
              </div>
            </div>
          ) : isClientCredentials ? (
            <div className="space-y-2" data-testid="connect-cc-fields">
              <p className="text-[11.5px] text-text3">Service-Identität: Client-ID + Client-Secret write-only → Vault. Der Master holt den Token selbst und erneuert ihn bei Ablauf.</p>
              <div className="space-y-1">
                <Label htmlFor="connect-cc-clientid">Client-ID</Label>
                <Input id="connect-cc-clientid" value={ccClientId} onChange={(e) => setCcClientId(e.target.value)} placeholder="service-user" className="mono text-[12px]" data-testid="connect-cc-clientid" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="connect-cc-clientsecret">Client-Secret</Label>
                <Input id="connect-cc-clientsecret" type="password" value={ccClientSecret} onChange={(e) => setCcClientSecret(e.target.value)} placeholder="••••••" className="mono text-[12px]" data-testid="connect-cc-clientsecret" />
              </div>
            </div>
          ) : isApiKey ? (
            <div className="space-y-1">
              <Label htmlFor="connect-value">API-Key / Token (write-only)</Label>
              <Input id="connect-value" type="password" value={value} onChange={(e) => setValue(e.target.value)} placeholder="sk-…" className="mono text-[12px]" data-testid="connect-value" />
            </div>
          ) : (
            /* #297: none / unavailable / unbekanntes method (neuerer Master) — ehrlich
               benennen statt ein Formular anbieten, das nichts bewirken kann. */
            <p className="text-[12.5px] text-text3" data-testid="connect-nothing">
              {spec.method === "unavailable"
                ? unavailableText(spec.reason)
                : "Für diesen Connector ist kein Credential zu hinterlegen."}
            </p>
          )}

          {error && <div className="text-xs text-danger" data-testid="connect-error">{error}</div>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>Abbrechen</Button>
          {isOAuth ? (
            <Button onClick={submitOAuth} disabled={startOAuth.isPending} data-testid="connect-oauth-start">
              {startOAuth.isPending ? "…" : "OAuth starten"}
            </Button>
          ) : isEnv ? (
            // Ohne Felder gäbe es nichts zu senden — dann lieber deaktivieren, statt einen
            // Klick mit „Mindestens eine Variable mit Wert" abzuweisen (der Widerspruch,
            // der den cli-Bug sichtbar gemacht hat).
            <Button
              onClick={submitEnv}
              disabled={connectEnv.isPending || envSchema.length === 0}
              data-testid="connect-env-submit"
            >
              {connectEnv.isPending ? "…" : "Verbinden"}
            </Button>
          ) : isLogin ? (
            <Button onClick={submitLogin} disabled={connectLogin.isPending || !loginUser.trim() || !loginPass} data-testid="connect-login-submit">
              {connectLogin.isPending ? "…" : "Verbinden"}
            </Button>
          ) : isClientCredentials ? (
            <Button
              onClick={submitClientCredentials}
              disabled={connectCc.isPending || !ccClientId.trim() || !ccClientSecret.trim()}
              data-testid="connect-cc-submit"
            >
              {connectCc.isPending ? "…" : "Verbinden"}
            </Button>
          ) : isApiKey ? (
            <Button onClick={submitApiKey} disabled={connectApiKey.isPending || !value.trim()} data-testid="connect-submit">
              {connectApiKey.isPending ? "…" : "Verbinden"}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
